import { Tag } from "@chakra-ui/react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { momentLocaleFor } from "../../common/formatting/i18nFormatters";
import { useCurrentMoment } from "../sourcing-events/portal/utils/useCurrentMoment";
import { BidStatus } from "../../autogen/bff-api";
import { deriveBidOutcome, type BidOutcome } from "./bidOutcome";

interface Props {
  status: BidStatus;
  // True once the event has reached an award decision. A still-SUBMITTED bid then
  // reads as "not selected" rather than "submitted".
  awarded?: boolean;
  biddingClosed?: boolean;
  standstillUntil?: string | null;
  missedRound?: boolean;
  declinedRound?: boolean;
  notContinued?: boolean;
  designContest?: boolean;
  size?: "sm" | "md" | "lg";
}

const meta: Record<BidOutcome | "missedRound" | "declinedRound" | "notContinued", { color: string; labelKey: string }> = {
  draft: { color: "gray", labelKey: "Draft" },
  notDelivered: { color: "orange", labelKey: "Not delivered" },
  submitted: { color: "green", labelKey: "Submitted" },
  awarded: { color: "teal", labelKey: "Awarded" },
  notSelected: { color: "purple", labelKey: "Not selected" },
  rejected: { color: "red", labelKey: "Rejected" },
  // Dynamic key for langen: t('Did not answer the round') t('Declined the round')
  missedRound: { color: "orange", labelKey: "Did not answer the round" },
  declinedRound: { color: "gray", labelKey: "Declined the round" },
  // Dynamic key for langen: t('Not continued') t('Not among the winners')
  notContinued: { color: "gray", labelKey: "Not continued" },
};

export const BidStatusTag = ({
  status,
  awarded = false,
  biddingClosed = false,
  standstillUntil,
  missedRound = false,
  declinedRound = false,
  notContinued = false,
  designContest = false,
  size = "sm",
}: Props) => {
  const { t, i18n } = useTranslation();
  const now = useCurrentMoment();
  const outcome = missedRound
    ? "missedRound"
    : declinedRound
      ? "declinedRound"
      : notContinued && status === BidStatus.Submitted
      ? "notContinued"
      : deriveBidOutcome(status, awarded, biddingClosed);
  const { color, labelKey } =
    designContest && outcome === "notSelected" ? { ...meta.notSelected, labelKey: "Not among the winners" } : meta[outcome];
  const inStandstill = outcome === "awarded" && standstillUntil != null && now.isBefore(standstillUntil);
  return (
    <Tag colorScheme={color} size={size} borderRadius="full" whiteSpace="nowrap">
      {inStandstill
        ? t("Awarded · standstill until {{date}}", { date: standstillEndDate(standstillUntil, i18n.language) })
        : t(labelKey)}
    </Tag>
  );
};

export const standstillEndDate = (value: string, language: string): string => {
  const locale = momentLocaleFor(language);
  return moment(value).locale(locale).format(locale === "nb" ? "D. MMM" : "D MMM");
};
